import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import Cropper from 'cropperjs';
import path from 'path';
import { guid } from '@/mixins/guid';
import { queuedWork } from '@/mixins/queued-work';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import dropsLoader from '@/mixins/components/drops-loader';
import vSelect from 'vue-select';
import { siteBuilder } from '@/mixins/site-builder';
import * as clipboard from 'clipboard-polyfill/dist/clipboard-polyfill.promise';

export default Vue.component('page-settings', {
  template: require('./template.html'),
  mixins: [
    guid,
    siteBuilder,
    uploader,
    queuedWork,
  ],
  computed: {
    ...mapGetters(['currentPage', 'currentSite']),
    savedPages() {
      // gather array of site pages except the one user is on
      let pages = _.filter(this.currentSite.pages, o => o._id !== this.currentPage._id);
      pages = _.map(pages, 'name');
      return pages;
    },
    templates() {
      const list = [];
      const { templates } = this.currentSite;
      _.forEach(templates, (t) => {
        if (t.status !== 'unpublished') {
          list.push({ _id: t._id, name: t.name });
        }
      });
      return list;
    },
    pageTemplate() {
      const { templateID } = this.currentPage;
      const template = this.templates.find(t => t._id === templateID);
      if (template) {
        return template.name;
      }
      return 'No template in use.';
    },
    descWarning() {
      let color = '';
      if (this.descCount > this.descLimit - 10 && this.descCount < this.descLimit - 1) {
        color = '#f98d40';
      }
      if (this.descCount >= this.descLimit - 1) {
        color = '#ff5f5f';
      }
      return color;
    },
  },
  components: {
    vSelect,
    uploadProgress,
    dropsLoader,
  },
  watch: {
    modal(open) {
      if (open) {
        this.setInitials();
      }
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    pagename(name) {
      this.changed.pagename = this.initial.pagename !== name;
    },
    'initial.pagename': function c(name) {
      this.changed.pagename = this.pagename !== name;
    },
    title(value) {
      this.changed.title = this.initial.title !== value;
    },
    'initial.title': function c(value) {
      this.changed.title = this.title !== value;
    },
    defaultTitle(value) {
      this.changed.defaultTitle = this.initial.defaultTitle !== value;
    },
    defaultDescription(value) {
      this.changed.defaultDescription = this.initial.defaultDescription !== value;
    },
    'meta.image': function c(value) {
      this.changed.meta = this.initial.meta.image !== value;
    },
    'initial.meta.image': function c(value) {
      this.changed.meta = this.meta.image !== value;
    },
    defaultMeta(value) {
      this.changed.defaultMeta = this.initial.defaultMeta !== value;
    },
    'initial.defaultTitle': function c(value) {
      this.changed.defaultTitle = this.defaultTitle !== value;
    },
    'initial.defaultDescription': function c(value) {
      this.changed.defaultDescription = this.defaultDescription !== value;
    },
    description(text) {
      this.changed.description = this.initial.description !== text;
    },
    'initial.description': function c(text) {
      this.changed.description = this.description !== text;
    },
    pageurl(url) {
      this.changed.pageurl = this.getUrl(this.initial.pageurl) !== url;
    },
    'initial.pageurl': function c(url) {
      this.changed.pageurl = this.pageurl !== this.getUrl(url);
    },
    openCropper(open) {
      if (open) {
        setTimeout(() => {
          const image = this.$refs.imageCropper;
          this.cropper = new Cropper(image, {
            aspectRatio: 40 / 21,
            dragMode: 'move',
            autoCropArea: 1,
            zoomable: false,
          });
        }, 300);
      }
    },
  },
  props: ['type', 'modal'],
  data() {
    return {
      confirmDel: false,
      confirmUnpublish: false,
      pagename: '',
      title: '',
      hoveredImg: false,
      metaimage: null,
      meta: {
        image: false,
      },
      description: false,
      pageurl: '',
      initialUrl: '',
      pageNameCheckError: false,
      removeTemplate: false,
      loading: false,
      usingTemplate: false,
      descCount: null,
      descLimit: 160,
      typing: false,
      pasteTO: null,
      defaultTitle: false,
      defaultDescription: false,
      defaultMeta: false,
      loaders: {
        pagename: false,
        title: false,
        meta: false,
        pageurl: false,
        description: false,
        template: false,
      },
      edit: {
        pagename: false,
        title: false,
        meta: false,
        pageurl: false,
        description: false,
        template: false,
      },
      changed: {
        pagename: false,
        title: false,
        meta: false,
        pageurl: false,
        description: false,
        template: false,
        defaultTitle: false,
        defaultDescription: false,
        defaultMeta: false,
      },
      initial: {
        pagename: false,
        title: false,
        meta: {
          image: false,
        },
        pageurl: false,
        description: false,
        defaultTitle: false,
        defaultDescription: false,
        defaultMeta: false,
        selectedTemplate: false,
        usingTemplate: false,
        blocks: [],
      },
      selectedTemplate: { name: 'None' },
      // cropper things
      localFile: null,
      openCropper: false,
      cropper: null,
    };
  },
  methods: {
    setInitials() {
      const page = this.currentPage;
      this.pagename = page.name;
      this.title = typeof page.title === 'undefined' ? `${this.currentSite.alias} | ${page.name}` : (page.title || '');
      this.defaultTitle = typeof page.defaultTitle === 'undefined' || page.defaultTitle;
      this.defaultDescription = page.defaultDescription;
      this.meta = typeof page.meta === 'undefined' ? { image: false } : page.meta;
      this.defaultMeta = typeof page.defaultMeta === 'undefined' || page.defaultMeta;
      this.pageurl = this.getUrl(page.url);
      this.description = page.description || '';
      this.initial.pagename = _.clone(page.name);
      this.initial.defaultTitle = this.defaultTitle;
      this.initial.defaultDescription = this.defaultDescription;
      this.initial.title = this.title;
      this.initial.pageurl = this.getUrl(_.clone(page.url));
      this.initial.description = _.clone(page.description);
      this.initial.meta = _.cloneDeep(this.meta);
      this.initial.defaultMeta = this.defaultMeta;
      // template enabling
      this.templateTouched = false;
      this.removeTemplate = false;
      const usingTemplate = page.templateID;
      this.usingTemplate = usingTemplate;
      if (usingTemplate) {
        this.selectedTemplate = _.find(this.templates, t => t._id === usingTemplate);
      }
      this.initial.blocks = _.cloneDeep(this.currentPage.blocks);
      this.initial.selectedTemplate = _.cloneDeep(this.selectedTemplate);
      this.initial.usingTemplate = usingTemplate;
      // reset confirms
      this.confirmDel = false;
    },
    savePageName() {
      if (this.changed.pagename) {
        this.$validator.validate('pagename', this.pagename).then(async (result) => {
          if (result) {
            const noDuplicates = await this.pageNameCheck(this.pagename);
            if (!noDuplicates) {
              return;
            }
            this.loaders.pagename = true;
            const notif = {
              id: this.guid(),
              type: 'success',
              message: `${_.upperFirst(this.currentPage.type)} saved successfully!`,
            };
            try {
              const value = _.clone(this.pagename);
              this.$store.dispatch('updateAllReferences', {
                item: this.currentPage,
                updates: [{ key: 'name', value }],
              });
              if (this.type === 'page') {
                await this.$lightning([{
                  name: 'updatePageAttr',
                  payload: {
                    siteId: this.currentSite._id,
                    pageId: this.currentPage._id,
                    updates: [{ key: 'name', value }],
                  },
                }]);
                this.$store.dispatch('setSiteMap');
                await this.$lightning([{
                  name: 'updateSiteMap',
                  payload: {
                    currentSite: this.currentSite,
                  },
                }]);
                if (this.currentPage.status !== 'unpublished') {
                  await this.callBuilder({
                    publish: 'page',
                    site: {
                      _id: this.currentSite._id,
                    },
                    page: {
                      _id: this.currentPage._id,
                    },
                  });
                }
              }
              if (this.type === 'template' || this.type === 'draft') {
                await this.$lightning([{
                  name: 'updatePageAttr',
                  payload: {
                    siteId: this.currentSite._id,
                    pageId: this.currentPage._id,
                    type: `${this.type}s`,
                    updates: [{ key: 'name', value }],
                  },
                }]);
              }
              if (this.type === 'dynamic page') {
                await this.$lightning([{
                  name: 'updateSiteAttr',
                  payload: {
                    siteId: this.currentSite._id,
                    attr: 'dynamic',
                    value: this.currentSite.dynamic,
                  },
                }]);
              }
              this.initial.pagename = value;
            } catch (e) {
              this.$log.error('error: ', e);
              notif.type = 'error';
              notif.message = `${e.message || e}. This has been reported.`;
              await this._reportError({
                error: e,
                fn: 'savePageName',
              });
            } finally {
              this.loaders.pagename = false;
              this.$store.dispatch('addNotification', notif);
            }
          }
        });
      }
    },
    savePageUrl() {
      if (this.changed.pageurl) {
        this.$validator.validate('url', this.pageurl).then(async (result) => {
          if (result) {
            this.loaders.pageurl = true;
            const notif = {
              id: this.guid(),
              type: 'success',
              message: `${_.upperFirst(this.currentPage.type)} saved successfully!`,
            };
            try {
              const value = this.buildPath(_.clone(this.pageurl));
              await this.$lightning([{
                name: 'updatePageAttr',
                payload: {
                  siteId: this.currentSite._id,
                  pageId: this.currentPage._id,
                  updates: [{ key: 'url', value }],
                },
              }]);
              this.$store.dispatch('updateAllReferences', {
                item: this.currentPage,
                updates: [{ key: 'url', value }],
              });
              // run site map stuff
              // when we update URL's, sitemap needs to rerun
              this.$store.dispatch('setSiteMap');
              // we send an updateSiteMap save since a url change can
              // effect other pages / templates etc.
              await this.$lightning([{
                name: 'updateSiteMap',
                payload: {
                  currentSite: this.currentSite,
                },
              }]);
              // regenerate entire site since url changes can potentially
              // effect any pages linking to the page
              if (this.currentPage.status !== 'unpublished') {
                await this.callBuilder({
                  publish: 'site',
                  site: {
                    _id: this.currentSite._id,
                  },
                  page: {
                    _id: this.currentPage._id,
                  },
                });
              }
              this.initial.pageurl = value;
              this.$log.debug('Successfully updated page url');
            } catch (e) {
              this.$log.error('error: ', e);
              notif.type = 'error';
              notif.message = `${e.message || e}. This has been reported.`;
              await this._reportError({
                error: e,
                fn: 'savePageUrl',
              });
            } finally {
              this.loaders.pageurl = false;
              this.$store.dispatch('addNotification', notif);
              this._trackEvent({
                type: 'change_page_url',
                meta: {
                  site_type: this.currentSite.type,
                  site_tier: this.currentSite.tier,
                  site_name: this.currentSite.alias,
                  page_name: this.currentPage.name,
                  page_url: this.currentPage.url,
                },
              });
            }
          }
        });
      }
    },
    async savePageItem(type) {
      const defaultType = `default${_.startCase(type)}`;
      if (this.changed[type] || this.changed[defaultType]) {
        this.loaders[type] = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: `${_.upperFirst(this.currentPage.type)} saved successfully!`,
        };
        try {
          const value = _.clone(this[type]);
          let cleaned = value;
          if (typeof value === 'string') {
            cleaned = value.trim() === '' ? false : value;
          }
          await this.$lightning([
            {
              name: 'updatePageAttr',
              payload: {
                siteId: this.currentSite._id,
                pageId: this.currentPage._id,
                updates: [
                  { key: type, value: cleaned },
                  { key: defaultType, value: this[defaultType] },
                ],
              },
            },
          ]);
          this.$store.dispatch('updateAllReferences', {
            item: this.currentPage,
            updates: [
              { key: type, value: cleaned },
              { key: defaultType, value: this[defaultType] },
            ],
          });
          this.initial[type] = value;
          this.initial[defaultType] = this[defaultType];
          this.changed[type] = false;
          this.changed[defaultType] = false;
          if (this.currentPage.status !== 'unpublished') {
            await this.callBuilder({
              publish: 'page',
              site: {
                _id: this.currentSite._id,
              },
              page: {
                _id: this.currentPage._id,
              },
            });
          }
        } catch (e) {
          this.$log.error('error: ', e);
          notif.type = 'error';
          notif.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'updatePageAttr',
          });
        } finally {
          this.loaders[type] = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    cancelItem(item, def) {
      let value = _.clone(this.initial[item]);
      if (item === 'pageurl') {
        value = this.getUrl(value);
      }
      this[item] = value;
      if (def) {
        const defaultType = `default${_.startCase(item)}`;
        this[defaultType] = this.initial[defaultType];
        if (item === 'meta') {
          this.$store.dispatch('setDropletVal', {
            drp: this.currentPage,
            key: 'meta',
            val: _.cloneDeep(value),
          });
        }
      }
    },
    canDelete(item) {
      // don't allow a page to be deleted if it has children in the sitemap
      let noChildren = true;
      if (item.type === 'page' && !item.isHomepage) {
        const page = this._goFind({
          entry: this.currentSite.siteMap,
          value: item._id,
        }).found;
        noChildren = (page && page.subPages.length < 1) || !page;
      }
      // if a template is in use, don't allow it to be deleted
      let tplNotInUse = true;
      if (item.type === 'template') {
        const pages = _.filter(this.currentSite.pages, page => page.templateID === item._id);
        tplNotInUse = pages.length < 1;
      }
      return (
        (!item.isHomepage && item.type !== 'template' && noChildren) ||
        (item.isHomepage && item.type === 'draft') ||
        (item.type === 'template' && tplNotInUse)
      );
    },
    getTipText(item) {
      let msg = '';
      if (!this.canDelete(item)) {
        if (item.type === 'template') {
          msg = 'Template is in use.';
        } else {
          msg = 'This page has sub pages in the site map.';
        }
      }
      return msg;
    },
    getUrl(path) {
      let p = path;
      if (p) {
        const split = p.split('/');
        // we know it's a path and not just a word
        if (split.length > 1) {
          const br = split.reverse();
          p = path.endsWith('/') ? br[1] : br[0];
        }
      } else {
        p = '';
      }
      return p;
    },
    buildPath(url) {
      let old = this.getUrl(this.initialUrl);
      old = new RegExp(old, 'gi');
      let newPath = this.initialUrl.replace(old, url);
      if (!newPath.endsWith('/')) {
        newPath = `${newPath}/`;
      }
      return newPath;
    },
    updateURL(e) {
      let val = e.currentTarget.value;
      // Adding in a stricter replace. This replaces anything that's not a num or letter,
      // So basically any funny characters user's might try and add won't be allowed.
      val = val.replace(/[^a-zA-Z0-9]/g, '-');
      this.pageurl = val;
    },
    pageNameCheck(name) {
      // check all pages for this site
      const valid = this.savedPages.includes(name);
      if (valid) {
        this.pageNameCheckError = 'There is another page with that name, please try another.';
      } else {
        this.pageNameCheckError = false;
      }
      return new Promise((resolve) => {
        resolve(!valid);
      });
    },
    updateTemplate(t) {
      this.selectedTemplate = t || { name: 'None' };
      this.toggleTemplate();
    },
    toggleTemplate() {
      // usingTemplate = v-model for checked "Enable"
      // add in the template content
      this.templateTouched = true;
      if (this.selectedTemplate.name === 'None' || !this.usingTemplate) {
        this.$store.dispatch('removeTemplate', {
          currentPage: this.currentPage,
        });
      }

      if (this.selectedTemplate &&
          this.usingTemplate &&
          this.selectedTemplate.name !== 'None' &&
          this.currentPage.templateID !== this.selectedTemplate._id) {
        this.$store.dispatch('removeTemplate', {
          currentPage: this.currentPage,
        });
        this.$store.dispatch('injectTemplate', {
          currentPage: this.currentPage,
          currentSite: this.currentSite,
          templateId: this.selectedTemplate._id,
        });
      }
    },
    async saveTemplate() {
      // this should always run for non templates as this adds a template
      // attachment to a page item.
      this.loaders.template = true;
      const notif = {
        id: this.guid(),
        type: 'success',
        message: 'Template saved successfully!',
      };
      try {
        // this should only run for pages, drafts & dynamic pages
        const { _id, forms } = this.currentSite;
        await this.$lightning([{
          name: 'savePageItem',
          payload: {
            item: this.currentPage,
            site: { _id, forms },
          },
        }]);
        // run queue addition
        if (this.currentPage.status !== 'unpublished') {
          this.addToQueuedWork({ page: this.currentPage });
        }
        this.templateTouched = false;
        this.initial.selectedTemplate = _.cloneDeep(this.selectedTemplate);
        this.initial.usingTemplate = this.usingTemplate;
        this.initial.blocks = _.cloneDeep(this.currentPage.blocks);
      } catch (e) {
        this.$log.error('error: ', e);
        notif.type = 'error';
        notif.message = `${e.message || e}.`;
        await this._reportError({
          error: e,
          fn: 'saveTemplate - Page settings',
        });
      } finally {
        this.loaders.template = false;
        this.$store.dispatch('addNotification', notif);
      }
    },
    async cancelTemplate() {
      const { currentPage } = this;
      const { blocks } = this.initial;
      this.$store.dispatch('runStateFn', {
        fn() {
          currentPage.blocks = _.cloneDeep(blocks);
        },
      });
      this.edit.template = false;
      this.templateTouched = false;
      this.usingTemplate = this.initial.usingTemplate;
      this.selectedTemplate = _.cloneDeep(this.initial.selectedTemplate);
    },
    async listS3Content(sources) {
      _.forEach(sources, async (src) => {
        const folders = src.split('/');
        const type = folders[folders.indexOf('_droplet-assets') + 1];
        const oldId = folders[folders.indexOf('_droplet-assets') + 2];
        try {
          const files = await this._s3fetchFiles(
            `${this._sitePath}/_droplet-assets/${type}/${oldId}/`,
          );
          // These are the files from the template droplet that we'd need to copy
          // into a new folder with the new droplet id, then we'd need to go and
          // update that droplet with the new sources
          this.$log.debug('files: ', files);
        } catch (err) {
          this.$log.error('error: ', err);
        }
      });
    },
    canUnpublish(item) {
      // we only want to allow pages to be unpublished
      const firstPass = !item.isHomepage && item.type === 'page' && item.status === 'published' && this._published;
      let noChildren = true;
      if (firstPass) {
        const page = this._goFind({
          entry: this.currentSite.siteMap,
          value: item._id,
        }).found;
        noChildren = (page && page.subPages.length < 1) || !page;
      }
      return firstPass && noChildren;
    },
    async unpublish() {
      if (this.canUnpublish(this.currentPage)) {
        this.loading = true;
        const n = {
          id: this.guid(),
          type: 'success',
          message: `Page: ${this.currentPage.name} unpublished successfully!`,
        };
        const trackData = {
          type: this.currentPage.type,
          name: this.currentPage.name,
          url: this.currentPage.url,
          blocks: this.currentPage.blocks.length,
        };
        try {
          const { _id } = this.currentSite;
          const { updatedPage } = await this.$lightning([{
            name: 'unPublishPageItem',
            payload: {
              site: { _id },
              item: {
                _id: this.currentPage._id,
              },
            },
          }]);
          this.$store.dispatch('setGlobalValue', {
            item: this.currentPage,
            key: 'status',
            value: 'unpublished',
          });
          const { pages } = this.currentSite;
          const page = pages.find(page => page._id === this.currentPage._id);
          if (page) {
            this.$store.dispatch('setGlobalValue', {
              item: page,
              key: 'status',
              value: 'unpublished',
            });
          }
          this.$store.dispatch('replaceQueuedPage', this.currentPage);
          this.removeFromQueuedWork({ page: this.currentPage });
          this.$log.debug('successfully unpublished page : ', updatedPage);
        } catch (e) {
          this.$log.error('error: ', e);
          n.type = 'error';
          n.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'unpublish',
          });
        } finally {
          this.loading = false;
          setTimeout(() => {
            this.$store.dispatch('addNotification', n);
            this.confirmUnpublish = false;
          }, 500);
          this._trackEvent({
            type: 'unpublish_page',
            meta: {
              site_name: this.currentSite.alias,
              site_type: this.currentSite.type,
              site_tier: this.currentSite.tier,
              page_name: trackData.name,
              page_url: trackData.url,
              page_type: trackData.type,
              blocks: trackData.blocks,
            },
          });
        }
        this.$emit('toggle-modal');
      }
    },
    async remove() {
      if (this.canDelete(this.currentPage)) {
        this.loading = true;
        const n = {
          id: this.guid(),
          type: 'success',
          message: `${this.currentPage.type}: ${this.currentPage.name} deleted successfully!`,
        };
        const trackData = {
          type: this.currentPage.type,
          name: this.currentPage.name,
          url: this.currentPage.url,
          blocks: this.currentPage.blocks.length,
        };
        try {
          let folder = false;
          if (this.currentPage.type === 'page') {
            // remove from state
            this.$store.dispatch('removeFromSiteMap', this.currentPage);
            // go delete the page folder in s3
            const path = this.getPath(this.currentPage.url);
            folder = `${this._customerFolder}/${this._sitePath}${path}`;
            // if this page is tied to a dynamic item, we need to reset it to null
            if (this.currentSite.dynamic && this.currentPage.dynamicItemId) {
              // Note, to search through list of ALL dynamic types, we can map all of
              // the keys in currentSite.dynamic, in case we allow multiple site types
              // in the future
              _.forOwn(this.currentSite.dynamic, (value, type) => {
                const item = _.find(
                  type.items, item => item._id === this.currentPage.dynamicItemId,
                );
                if (item) {
                  this.$store.dispatch('setDropletVal', {
                    drp: item,
                    key: 'pageId',
                    val: null,
                  });
                }
                const published = _.find(
                  type.published, p => p._id === this.currentPage.dynamicItemId,
                );
                if (published) {
                  this.$store.dispatch('setDropletVal', {
                    drp: published,
                    key: 'pageId',
                    val: null,
                  });
                }
              });
            }
            // if this page is tied to a group item, we need to reset it to null
            if (this.currentSite.dynamic && this.currentPage.groupItemId) {
              // Note, to search through list of ALL dynamic types, we can map all of
              // the keys in currentSite.dynamic, in case we allow multiple site types
              // in the future
              _.forOwn(this.currentSite.dynamic, (value, type) => {
                const group = _.find(
                  type.groups, group => group._id === this.currentPage.groupItemId,
                );
                if (group) {
                  this.$store.dispatch('setDropletVal', {
                    drp: group,
                    key: 'pageId',
                    val: null,
                  });
                }
              });
            }
          }
          if (this.currentPage.type !== 'draft') {
            // remove any counted droplets that are on this page
            this.removeCountedDroplets('forms');
          }
          // delete page from state
          if (this.currentPage.type === 'dynamic page') {
            const pageId = this.currentPage._id;
            _.forOwn(this.currentSite.dynamic, (value) => {
              // if dynamic page being deleted is set as template for published items,
              // we need to reset that value to null
              if (value.page.dynamicId === this.currentPage._id) {
                this.$store.dispatch('setDropletVal', {
                  drp: value.page,
                  key: 'dynamicId',
                  val: null,
                });
              }
              if (value.page.groupPageId === this.currentPage._id) {
                this.$store.dispatch('setDropletVal', {
                  drp: value.page,
                  key: 'groupPageId',
                  val: null,
                });
              }
              // remove item from published list
              const index = _.findIndex(value.pages, p => p._id === this.currentPage._id);
              if (index >= 0) {
                this.$store.dispatch('spliceDropletVal', {
                  array: value.pages,
                  index,
                });
              }
            });
            await this.$lightning([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                pageId,
                attr: 'dynamic',
                value: this.currentSite.dynamic,
              },
            }]);
          } else {
            await this.$store.dispatch('removePage', this.currentPage);
            const { _id, forms, siteMap } = this.currentSite;
            await this.$lightning([{
              name: 'deletePageItem',
              payload: {
                site: { _id, forms, siteMap },
                item: {
                  _id: this.currentPage._id,
                  type: this.currentPage.type,
                },
                folder,
              },
            }]);
            this.$log.debug('successfully removed page from db');
          }
          // reset currentPage to the homepage
          const homepage = _.find(this._currentSite.pages, {
            isHomepage: true,
          });
          const { item } = await this.checkQueuedWork({ page: homepage });
          this.$store.dispatch('setOriginalPage', item);
          await this.$store.dispatch('setCurrentPage', item);
        } catch (e) {
          this.$log.error('error: ', e);
          n.type = 'error';
          n.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'remove',
          });
        } finally {
          this.loading = false;
          setTimeout(() => {
            this.$store.dispatch('addNotification', n);
            this.confirmDel = false;
          }, 500);
          this._trackEvent({
            type: 'delete_page',
            meta: {
              site_name: this.currentSite.alias,
              site_type: this.currentSite.type,
              site_tier: this.currentSite.tier,
              page_name: trackData.name,
              page_url: trackData.url,
              page_type: trackData.type,
              blocks: trackData.blocks,
            },
          });
        }
        this.$emit('toggle-modal');
      }
    },
    removeCountedDroplets(list) {
      if (this.currentPage[list] && this.currentSite[list]) {
        _.forEach(this.currentPage[list], (_id) => {
          const index = _.findIndex(this.currentSite[list], f => f === _id);
          if (index >= 0) {
            this.$store.dispatch('spliceDropletVal', {
              array: this.currentSite[list],
              index,
            });
          }
        });
      }
    },
    getPath(url) {
      // Since the sitemap makes links take a depth, we need to make sure
      // it is properly doing so.
      let path = url;
      if (!path.endsWith('/') && path !== '/') {
        // remove last "/" if there
        path = `${path}/`;
      }
      if (!path.startsWith('/') && path !== '/') {
        // remove the first "/" if there
        path = `/${path}`;
      }
      if (path.trim() === '') {
        path = '/';
      }
      return path;
    },
    confirmDelete() {
      if (this.canDelete(this.currentPage)) {
        this.confirmDel = true;
      }
    },
    formatErrors(ers) {
      let string = '';
      _.forEach(ers, (er) => {
        string = string += ` ${er}`;
      });
      return string;
    },
    inputText() {
      this.typing = true;
      this.charCount();
    },
    pasteText() {
      this.typing = true;
      clipboard.readText().then(() => {
        this.charCount();
        // prevent users from pasting in copy longer than limit
        clearTimeout(this.pasteTO);
        this.pasteTO = setTimeout(() => {
          this.charCount();
        }, 250);
      })
        .catch(e => this.$log.error(e));
    },
    charCount() {
      this.descCount = this.description.length;
    },
    // meta stuff
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    destroyCropper() {
      this.openCropper = false;
      this.localFile = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    openUpload(type) {
      this.$refs[type].click();
    },
    async uploadCropped() {
      try {
        const canvas = this.cropper.getCroppedCanvas({
          width: 1200,
          height: 630,
          imageSmoothingQuality: 'high',
        });
        const blob = await this.getBlob(canvas);
        // send off to our s3 upload mixin
        const filePath = `/_meta/${this.localFile}`;
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: blob,
        });
        const meta = this.currentPage.meta ? _.cloneDeep(this.currentPage.meta) : {};
        meta.image = filePath;
        this.$store.dispatch('setDropletVal', {
          drp: this.currentPage,
          key: 'meta',
          val: meta,
        });
        this.meta = meta;
        this.changed.meta = true;
        this.destroyCropper();
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'metaImage page uploadCropped',
          });
        }
      }
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // gather naming information
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `drz_metaimage_${_.trim(fileName)}`;
              fileName = _.replace(fileName, /\s+/g, '_');
              this.localFile = fileName;
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'metaImage openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    clearMetaImg() {
      const meta = _.cloneDeep(this.currentSite.meta);
      meta.image = false;
      this.$store.dispatch('setDropletVal', {
        drp: this.currentPage,
        key: 'meta',
        val: meta,
      });
      this.meta = _.cloneDeep(meta);
      if (!this.initial.meta) {
        this.changed.meta = false;
      } else {
        this.changed.meta = true;
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/page-settings/index.js